import { Link } from '@tanstack/react-router';
import { AlertTriangle, Clock, Users } from 'lucide-react';

import { getInvoiceStatusBadge } from '~features/payment/utils/hourly-tuition-display-status';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~shared/shadcn/ui/accordion';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Separator } from '~shared/shadcn/ui/separator';

interface ProcessingRecord {
  processedAt: string;
  processedAtFormatted: string;
  studentCount: number;
  postingCount?: number;
  students: Array<{
    studentId: string;
    studentName: string;
    invoiceId: string | null;
    invoiceStatus: string | null;
  }>;
}

interface WarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  processingHistory?: Array<ProcessingRecord> | null;
}

export function WarningDialog({
  open,
  onOpenChange,
  title = 'Already Processed',
  message,
  onConfirm,
  onCancel,
  confirmText = 'Proceed',
  cancelText = 'Cancel',
  variant = 'destructive',
  processingHistory,
}: WarningDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[600px]'>
        <div className='shrink-0 px-6 pt-6'>
          <DialogHeader>
            <div className='flex items-center gap-3'>
              <div className='flex size-10 items-center justify-center rounded-full bg-orange-100'>
                <AlertTriangle className='size-5 text-orange-600' />
              </div>
              <DialogTitle className='text-lg font-semibold text-gray-900'>
                {title}
              </DialogTitle>
            </div>
            <DialogDescription className='text-sm text-gray-600'>
              {message}
            </DialogDescription>
          </DialogHeader>
        </div>

        {processingHistory && processingHistory.length > 0 && (
          <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
            <Separator className='mb-4' />
            <div className='space-y-3'>
              <h4 className='flex items-center gap-2 text-sm font-medium text-gray-900'>
                <Clock className='size-4' />
                Processing History
              </h4>
              <Accordion type='multiple' className='space-y-3'>
                {processingHistory.map((record) => {
                  const accordionKey = `${record.processedAtFormatted}-${record.postingCount ?? 1}`;

                  return (
                  <AccordionItem
                    key={accordionKey}
                    value={accordionKey}
                    className='rounded-lg border bg-gray-50 px-3'
                  >
                    <AccordionTrigger className='py-3 hover:no-underline'>
                      <div className='flex flex-1 items-center justify-between gap-3 pr-2'>
                        <div className='text-left'>
                          <span className='text-sm font-medium text-gray-900'>
                            {record.processedAtFormatted}
                          </span>
                          {record.postingCount != null && record.postingCount > 1 && (
                            <p className='text-xs text-gray-500'>
                              {record.postingCount} postings consolidated
                            </p>
                          )}
                        </div>
                        <div className='flex shrink-0 items-center gap-1 text-xs text-gray-500'>
                          <Users className='size-3' />
                          {record.studentCount} student
                          {record.studentCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className='space-y-2'>
                        <div className='text-sm font-medium text-gray-900'>
                          Students:
                        </div>
                        <div className='max-h-48 space-y-2 overflow-y-auto pr-1'>
                          {record.students.map((student) => (
                            <div
                              key={student.studentId}
                              className='flex items-center justify-between rounded-md border bg-white p-2'
                            >
                              <div className='flex items-center gap-2'>
                                <Link
                                  to={`/students/$studentId/new-payments`}
                                  params={{ studentId: student.studentId }}
                                  className='text-sm text-gray-900 underline'
                                >
                                  {student.studentName}
                                </Link>
                              </div>
                              <div className='flex items-center gap-2'>
                                {getInvoiceStatusBadge(student.invoiceStatus)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>
        )}

        <DialogFooter className='shrink-0 gap-2 border-t px-6 py-4'>
          <Button variant='outline' onClick={handleCancel} className='flex-1'>
            {cancelText}
          </Button>
          <Button variant={variant} onClick={handleConfirm} className='flex-1'>
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
